import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, GraduationCap, FileEdit, UserCog, ScrollText, LogOut, ExternalLink, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/admin/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/waitlist", label: "Academy waitlist", icon: GraduationCap },
  { to: "/admin/content", label: "Website content", icon: FileEdit },
  { to: "/admin/account", label: "Account & security", icon: UserCog },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const links = (
    <nav className="flex flex-col gap-1" data-testid="admin-sidebar-nav">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${isActive ? "bg-amber-400 text-[#0B1528] font-semibold" : "text-slate-400 hover:bg-white/5 hover:text-white"}`
          }
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9] md:grid md:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="hidden md:flex flex-col bg-[#0B1528] p-6 sticky top-0 h-screen">
        <Logo dark className="mb-10" />
        {links}
        <div className="mt-auto pt-6 border-t border-white/10">
          <p className="text-sm font-semibold text-white truncate" data-testid="admin-user-name">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.username}</p>
          <div className="mt-4 flex gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs text-slate-300 hover:bg-white/5" data-testid="admin-view-site">
              <ExternalLink className="h-3.5 w-3.5" /> Site
            </a>
            <button onClick={async () => { await logout(); nav("/admin"); }} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs text-slate-300 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-[#0B1528] px-4 py-3">
        <Logo dark />
        <button onClick={() => setOpen(!open)} className="text-white p-2" aria-label="Menu" data-testid="admin-mobile-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
      </header>
      {open && (
        <div className="md:hidden bg-[#0B1528] px-4 pb-5" data-testid="admin-mobile-nav">
          {links}
          <button onClick={async () => { await logout(); nav("/admin"); }} className="mt-3 w-full rounded-full border border-white/10 px-3 py-2 text-xs text-slate-300" data-testid="admin-mobile-logout">Sign out</button>
        </div>
      )}

      <main className="p-5 sm:p-8 lg:p-10 max-w-6xl w-full">
        <Outlet />
      </main>
    </div>
  );
}
